import { memo, useCallback, useEffect, useRef } from 'react'
import {
  useCloseActiveChat,
  useCloseMentions,
  useOpenMentions,
  useSetChatInput,
  MentionableItem,
  useSetMessageMentions,
  useSetActiveChat,
  useSubscription,
  useLoadMessages,
  useSubmitMessage,
  useActiveChatId,
} from '@renderer/chat'
import OverlayIpcRenderer from '@renderer/ipc/OverlayIpc.renderer.ts'
import { ChatAttachment } from '@renderer/chat/types'
import { GlobalEvents, GlobalState, GlobalStore } from '@highlight/ipc'
import { ChatInputWithMentionsPayload } from '@renderer/types/events'
import { useGlobalEvent } from '@highlight/ipc/react'
import { useResetChatContext } from '@renderer/chat/store/atoms'
import { DetectedMeeting, GoogleMeetMeeting } from '@highlight/common'
import { useHotkeyEvent } from '@renderer/hooks/useHotkeyEvent.ts'
import { useStartNewChatTimeout } from '@renderer/hooks/settings/useStartNewChatTimeout.ts'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer.ts'
import { useAudioNoteSavedNote } from '@renderer/hooks/audio/use-audio-note-selectors'
import { useStartAudioNote } from '@renderer/hooks/useStartAudioNote'
import { usePrepareMeeting } from '@renderer/hooks/usePrepareMeeting'
import { useSetSidepanelState, useSidepanelState } from '@renderer/overlay/atoms'
import { useShowPaywall } from '@renderer/components/pricing/use-show-paywall'
import { useSetSidepanelHotkeyOpened, useSetChatInputPlaceholder } from '@renderer/overlay/Sidepanel/atoms'
import { useViewActions, useResetViews } from '@renderer/overlay/Sidepanel/view-atoms'
import { setOverlayInteractive } from '@highlight/ui'
import { MEETING_LIMIT_REACHED_EVENT, type MeetingLimitReachedPayload } from '@shared/meeting-gating-events'
import { useMeetingLimitRoute } from '@renderer/chat/hooks/use-meeting-limit-route'
import { resolveMeetingRefusalSurface } from '@renderer/chat/utils/billingUtils'

type SetChatInputOptions = { mentions: MentionableItem[]; resetChat?: boolean }

// How long to keep chat input active after closing (in minutes)
const INPUT_PERSIST_MINUTES = 5

export const AssistantEventHandler = memo(() => {
  const sidepanelState = useSidepanelState()
  const openMentions = useOpenMentions()
  const closeMentions = useCloseMentions()
  const activeChatId = useActiveChatId()
  const closeActiveChat = useCloseActiveChat()
  const setChatInput = useSetChatInput()
  const setMentions = useSetMessageMentions()
  const setActiveChatId = useSetActiveChat()
  const { subscription, refetchSubscription } = useSubscription()
  const meetingLimitRoute = useMeetingLimitRoute()
  const resetChatContext = useResetChatContext()
  const submitMessage = useSubmitMessage()
  const [startNewChatTimeout] = useStartNewChatTimeout()
  const clearChatTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const persistInputTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const savedNote = useAudioNoteSavedNote()
  const { startAudioNote } = useStartAudioNote()

  // Sidepanel state management
  const setSidepanelState = useSetSidepanelState()
  const setHotkeyOpened = useSetSidepanelHotkeyOpened()
  const showPaywall = useShowPaywall()
  const { openView, closeAllViews } = useViewActions()
  const resetViews = useResetViews()
  const setChatInputPlaceholder = useSetChatInputPlaceholder()

  // Initialize message loading hook
  useLoadMessages()

  // Listen to escape events from hotkey
  const onEscapeKey = useCallback(() => {
    if (savedNote) {
      setOverlayInteractive(false)
      AmplitudeRenderer.trackEvent('Audio Note Action', {
        action: 'finish',
        trigger: 'escape key',
      })
    }

    GlobalState.set('typing.cancelled', true)
  }, [savedNote])
  useHotkeyEvent('Escape', onEscapeKey)

  /**
   * Handle events from new chat being pressed from the tray menu.
   */
  const onTrayNewChatPressed = useCallback(async () => {
    // Open sidepanel instead of expanding Assistant
    setHotkeyOpened(true)
    setSidepanelState('open')
    // Close any active chat and drawers, return to sidepanel home
    closeActiveChat()
    closeAllViews()
  }, [closeActiveChat, setHotkeyOpened, setSidepanelState, closeAllViews])
  useGlobalEvent('trayNewChatPressed', onTrayNewChatPressed)

  /**
   * Handle events from new meeting note being pressed from the tray menu.
   */
  const onTrayNewMeetingNotePressed = useCallback(async () => {
    startAudioNote({ source: 'tray' })
  }, [startAudioNote])
  useGlobalEvent('trayNewMeetingNotePressed', onTrayNewMeetingNotePressed)

  /**
   * Handle events from Highlight Chat.
   */
  const onNewOrOpenChat = useCallback(
    (chatId?: string) => {
      // Open sidepanel instead of expanding Assistant
      setHotkeyOpened(true)
      setSidepanelState('open')

      if (chatId && chatId !== 'new') {
        setActiveChatId(chatId)
        // Opening the Chat View replaces the whole history stack, so whatever
        // View was up (meeting, action, etc.) is cleared.
        openView('chat', { chatId })
      } else {
        // New chat: go to sidepanel home and focus input
        closeActiveChat()
        closeAllViews()
        resetChatContext()
        setChatInput('')
        closeMentions()
        setChatInputPlaceholder(null)
      }
    },
    [
      closeActiveChat,
      resetChatContext,
      setChatInput,
      closeMentions,
      setHotkeyOpened,
      setSidepanelState,
      closeAllViews,
      openView,
    ],
  )
  useGlobalEvent('openChat:assistant', (chatId) => onNewOrOpenChat(chatId))

  // Initialize meeting preparation hook
  const prepareMeeting = usePrepareMeeting(onNewOrOpenChat)

  /**
   * Handle clear events like 'logout'
   */
  const onClearChat = useCallback(() => {
    closeActiveChat()
    // Reset rather than close: the signed-out session's Views can't stay in
    // forward history, where the header chevron would walk right back into its
    // chats, meetings and drafts (HIG-5479 closed the same hole for the
    // persisted chat drawer).
    resetViews()
    resetChatContext()
    setChatInput('')
    closeMentions()
  }, [closeActiveChat, resetViews, resetChatContext, setChatInput, closeMentions])

  useGlobalEvent('newChat', onNewOrOpenChat)
  useGlobalEvent('auth:signout_completed', onClearChat)

  const onSetChatInput = useCallback(
    (input: string, options?: SetChatInputOptions) => {
      const hasMentions = !!options?.mentions?.length
      if (hasMentions) {
        setMentions(options.mentions)
      }
      if (options?.resetChat) {
        setActiveChatId(undefined)
      }

      setChatInput(input)
      OverlayIpcRenderer.focusHighlightWindow()

      if (input === '@') {
        // The composer now holds the `@`, so the query is typed there.
        setTimeout(() => {
          openMentions()
        }, 200)
      }

      // Open sidepanel instead of expanding Assistant
      setHotkeyOpened(true)
      setSidepanelState('open')
    },
    [setChatInput, openMentions, setMentions, setHotkeyOpened, setSidepanelState],
  )
  useGlobalEvent('setChatInput', onSetChatInput)

  useGlobalEvent('setChatInputWithMentions', (data: ChatInputWithMentionsPayload) => {
    onSetChatInput(data.input, { mentions: data.mentions })
  })

  useGlobalEvent('prepareScheduledMeeting:assistant', prepareMeeting)

  // Update sub state in global store
  useEffect(() => {
    GlobalStore.user().set('subscription', subscription)
  }, [subscription])

  // Listen for subscription status events
  useGlobalEvent('subscription-status', async () => {
    refetchSubscription()
  })

  // Start timeout for clearing chat after collapse
  useEffect(() => {
    if (!activeChatId || startNewChatTimeout === 0 || startNewChatTimeout === Infinity) {
      if (clearChatTimeoutRef.current) {
        clearTimeout(clearChatTimeoutRef.current)
        clearChatTimeoutRef.current = undefined
      }
      return
    }
    if (sidepanelState !== 'open') {
      if (!clearChatTimeoutRef.current) {
        clearChatTimeoutRef.current = setTimeout(
          () => {
            console.log('Starting new chat automatically due to timeout')
            closeActiveChat()
            clearChatTimeoutRef.current = undefined
          },
          startNewChatTimeout * 60 * 1000,
        )
      }
    } else {
      if (clearChatTimeoutRef.current) {
        clearTimeout(clearChatTimeoutRef.current)
        clearChatTimeoutRef.current = undefined
      }
    }
  }, [sidepanelState, activeChatId, closeActiveChat, startNewChatTimeout])

  const onPrepareAndSubmitIntegration = useCallback(
    (payload: { prefillText: string; mentions: MentionableItem[]; description?: string }) => {
      const { prefillText, mentions, description } = payload
      console.log('[AssistantEventHandler] Received prepareAndSubmitIntegration', payload)
      // Open sidepanel instead of expanding Assistant
      setHotkeyOpened(true)
      setSidepanelState('open')

      const finalAttachments: ChatAttachment[] = [{ attachment: { type: 'selectedText', text: description ?? '' } }]

      submitMessage(prefillText, { mentions, attachments: finalAttachments, isNewChat: true })
      GlobalEvents.emit('assistant:show')
    },
    [submitMessage, setHotkeyOpened, setSidepanelState],
  )
  useGlobalEvent('assistant:prepareAndSubmitIntegration', onPrepareAndSubmitIntegration)

  // Manage input persistence timeout when assistant is collapsed
  useEffect(() => {
    if (sidepanelState !== 'open') {
      // Clear any existing timeout first
      if (persistInputTimeoutRef.current) {
        clearTimeout(persistInputTimeoutRef.current)
      }

      // Set new timeout to clear input after INPUT_PERSIST_MINUTES
      persistInputTimeoutRef.current = setTimeout(
        () => {
          setChatInput('')
          persistInputTimeoutRef.current = undefined
        },
        INPUT_PERSIST_MINUTES * 60 * 1000,
      )
    } else {
      // Cancel timeout if assistant is expanded again
      if (persistInputTimeoutRef.current) {
        clearTimeout(persistInputTimeoutRef.current)
        persistInputTimeoutRef.current = undefined
      }
    }

    // Cleanup on unmount
    return () => {
      if (persistInputTimeoutRef.current) {
        clearTimeout(persistInputTimeoutRef.current)
      }
    }
  }, [sidepanelState, setChatInput])

  // A meeting start was refused by a billing gate (server 403 or the client
  // meeting-hours check), so no recording started and there is no
  // local-transcription fallback. A paid v2 tier gets a toast from
  // use-meeting-toasts (see resolveMeetingRefusalSurface); the panel never
  // opens on its own for it. Everyone else is brought to the panel, where
  // the meeting-hours top-up or the plan promo is docked on home.
  useGlobalEvent(MEETING_LIMIT_REACHED_EVENT, (payload: MeetingLimitReachedPayload) => {
    if (resolveMeetingRefusalSurface(payload.reason, meetingLimitRoute) !== 'paywall') {
      return
    }
    setHotkeyOpened(true)
    showPaywall('hours')
  })

  useGlobalEvent('meeting:attempt-auto-transcribe', (meeting: DetectedMeeting | GoogleMeetMeeting) => {
    const onSuccessfulStart = () => {
      GlobalEvents.emit('meeting:started-with-transcription', meeting)
    }

    startAudioNote({ source: 'assistant', meeting, onSuccessfulStart })
  })

  return null
})

AssistantEventHandler.displayName = 'AssistantEventHandler'
